import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Sparkline } from './Sparkline';

/**
 * KPI tile matching Design/app/page-dashboard.jsx prototype:
 *   - hairline-bound surface, 2 px left bar in brand-olive
 *   - small label, large number with optional unit, sublabel, optional spark
 *   - severity variants change the bar + spark colour, never the label text
 *     (build guide §3.17)
 */
interface KpiProps {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  spark?: { points: number[]; area?: boolean };
  variant?: 'ok' | 'warn' | 'crit';
  className?: string;
}

export function Kpi({ label, value, detail, spark, variant = 'ok', className }: KpiProps) {
  const barColour =
    variant === 'crit'
      ? 'bg-sev-crit'
      : variant === 'warn'
        ? 'bg-sev-warn'
        : 'bg-mb-olive';
  const sparkColour =
    variant === 'crit'
      ? 'text-sev-crit'
      : variant === 'warn'
        ? 'text-sev-warn'
        : 'text-mb-olive';
  return (
    <section className={cn('relative bg-bg-panel border border-rule p-5', className)}>
      <span className={cn('absolute left-0 top-0 bottom-0 w-0.5', barColour)} aria-hidden="true" />
      <p className="text-[10px] tracking-[0.20em] uppercase font-bold text-fg-muted">{label}</p>
      <p
        className="mt-3 font-light tracking-[0.01em] tabular-nums"
        style={{ fontSize: '36px', lineHeight: '1.05' }}
      >
        {value}
      </p>
      {detail && (
        <div className="text-caption text-fg-muted mt-1.5 leading-[1.4] min-h-[16px]">
          {detail}
        </div>
      )}
      {spark && (
        <div className={cn('mt-2.5 h-[28px]', sparkColour)}>
          <Sparkline points={spark.points} area={spark.area} />
        </div>
      )}
    </section>
  );
}
